import {
	BusyIndicator,
	ListItem,
	MentionInput,
	MentionInputSuggestion,
	MentionInputSuggestionBoxHandle,
	MentionInputSuggestionBoxProps,
	NoResults,
	PersonAvatar
} from "client/Components";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./MentionSuggestionBox.module.less";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { useDebouncedValue } from "client/Hooks";
import {
	MentionData,
	MentionKind,
	userMentionType
} from "client/Components/MentionInput/converters";
import { UserIdentityModel, getUserProfileImageUrl } from "client/Api/UserProfile/service";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import classNames from "classnames";

export type MentionSuggestionBoxProps = Omit<MentionInputSuggestionBoxProps, "onClose"> & {
	findUsers: (
		searchTerm: string,
		amount: number,
		signal: AbortSignal
	) => Promise<readonly UserIdentityModel[]>;
	variant?: "inline" | "dropover";
};

export const MentionSuggestionBox = forwardRef(
	(props: MentionSuggestionBoxProps, ref: Ref<MentionInputSuggestionBoxHandle>): ReactElement => {
		const {
			findUsers,
			mentionType,
			searchTerm,
			onSelectItem,
			onEmptyStatusChange,
			variant = "inline",
			trackingId
		} = props;

		const [suggestions, setSuggestions] = useState<readonly Person[]>([]);
		const [isBusy, setIsBusy] = useState<boolean>(false);
		const debouncedSearchTerm: string = useDebouncedValue(searchTerm, 250) ?? "";

		useEffect(
			(): EffectWithDestructorResult => {
				const fetchData = async (signal: AbortSignal): Promise<void> => {
					try {
						if (!debouncedSearchTerm) {
							setIsBusy(false);
							return;
						}

						setIsBusy(true);
						let result: readonly Person[];

						switch (mentionType) {
							case userMentionType:
								const users: readonly UserIdentityModel[] = await findUsers(
									debouncedSearchTerm,
									10,
									signal
								);

								result = users.map((x): Person => {
									const {
										computedDisplayName: displayName,
										initials
									} = getComputedDisplayName({
										firstName: x.firstName,
										lastName: x.lastName
									});

									return {
										userId: x.userId,
										avatarUrl: x.avatarId !== null
											? getUserProfileImageUrl(x.avatarId)
											: null,
										displayName,
										jobTitle: x.jobTitle,
										initials
									};
								});

								onEmptyStatusChange(
									debouncedSearchTerm,
									result.length === 0
								);

								break;
							default:
								throw new Error(`Not supported mention type '${mentionType}'`);
						}

						setSuggestions(result);
						setIsBusy(false);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						console.error(e);
						setIsBusy(false);
					}
				};

				const cancellation = new AbortController();
				fetchData(cancellation.signal);

				return () => cancellation.abort();
			},
			[
				debouncedSearchTerm,
				mentionType,
				onEmptyStatusChange,
				findUsers
			]
		);

		useImperativeHandle(
			ref,
			(): MentionInputSuggestionBoxHandle => ({
				getTopSuggestion(): MentionInputSuggestion | null {
					if (suggestions.length === 0) {
						return null;
					}

					return convertToMentionInputSuggestion(suggestions[0]);
				},
			}),
			[suggestions]
		);

		const selectItemHandler = useCallback(
			(index: number): void => {
				onSelectItem(
					convertToMentionInputSuggestion(suggestions[index])
				);
			},
			[onSelectItem, suggestions]
		);

		const renderContent = (): ReactElement => {
			const shouldRenderBusyIndicator: boolean = isBusy || debouncedSearchTerm !== searchTerm;
			if (shouldRenderBusyIndicator) {
				return <BusyIndicator />;
			}

			if (searchTerm === "") {
				return (
					<NoResults
						icon="user"
						title={localize("_EnterAName")}
						message={localize("_PleaseEnterTheNameOfThePersonYouWantToMention")}
						height="fill"
					/>
				);
			}

			if (suggestions.length === 0) {
				return (
					<NoResults
						trackingId="no-search-results"
						icon="magnifying-glass"
						title={localize("_NoSearchResults")}
						message={localize("_PleaseTrySearchingWithAnotherTerm")}
						height="fill"
					/>
				);
			}

			return (
				<MentionInput.SuggestionList trackingId={trackingId}>
					{suggestions.map((x, i) => (
						<MentionInput.SuggestionList.Item
							key={x.userId}
							index={i}
							onSelect={selectItemHandler}
						>
							<ListItem trackingId="mention-suggestion-list-item">
								<ListItem.Content
									onClick={() => selectItemHandler(i)}
									thumbnail={
										<PersonAvatar
											initials={x.initials}
											imageSrc={x.avatarUrl}
										/>
									}
								>
									<ListItem.Title>
										{x.displayName}
									</ListItem.Title>
									<ListItem.SubTitle>
										{x.jobTitle}
									</ListItem.SubTitle>
								</ListItem.Content>
							</ListItem>
						</MentionInput.SuggestionList.Item>
					))}
				</MentionInput.SuggestionList>
			);
		};

		return (
			<div
				className={classNames(
					styles.mentionMenu,
					{ [styles.mentionMenu_dropover]: variant === "dropover" }
				)}
			>
				{renderContent()}
			</div>
		);
	}
);
MentionSuggestionBox.displayName = "MentionSuggestionBox";

export type Person = {
	userId: number;
	displayName: string;
	initials: string;
	jobTitle: string | null;
	avatarUrl: string | null;
};

export function convertToMentionInputSuggestion(person: Person): MentionInputSuggestion {
	return {
		data: person.userId.toString(),
		text: person.displayName
	};
}

export function convertToMentionData(suggestion: MentionInputSuggestion): MentionData {
	const userId: number = Number(suggestion.data);
	if (isNaN(userId)) {
		throw new Error("Expected userId as data");
	}

	return {
		kind: MentionKind.User,
		userId,
		displayName: suggestion.text
	};
}
